import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';

import {
  BondApiItem,
  BondRow,
  BondsApiResponse,
  BondsPage,
  BondsQueryParams,
} from './bonds.types';
import { apiUrl } from '../../auth/api-url';

@Injectable({
  providedIn: 'root',
})
export class BondsService {
  private readonly endpoint = apiUrl('bond');
  private readonly defaultPageNumber = 1;
  private readonly defaultPageSize = 25;
  private readonly minPageSize = 1;
  private readonly maxPageSize = 200;

  constructor(private readonly http: HttpClient) {}

  getBonds(query: BondsQueryParams): Observable<BondsPage> {
    const normalizedPageNumber = this.normalizePageNumber(query.pageNumber);
    const normalizedPageSize = this.normalizePageSize(query.pageSize);

    let params = new HttpParams()
      .set('pageNumber', String(normalizedPageNumber))
      .set('pageSize', String(normalizedPageSize));

    const search = this.normalizeText(query.search);
    if (search) {
      params = params.set('search', search);
    }

    if (query.sortBy) {
      params = params.set('sortBy', query.sortBy);
    }

    if (query.sortDirection) {
      params = params.set('sortDirection', query.sortDirection);
    }

    if (query.view) {
      params = params.set('view', query.view);
    }

    return this.http.get<unknown>(this.endpoint, { params }).pipe(
      map((response) => this.normalizeResponse(response, normalizedPageNumber, normalizedPageSize)),
    );
  }

  private normalizeResponse(
    response: unknown,
    fallbackPageNumber: number,
    fallbackPageSize: number,
  ): BondsPage {
    const record = this.toRecord(response);
    const items = this.toItems(record['items']).map((item) => this.normalizeItem(item));
    const totalRecords = this.toNonNegativeNumber(record['totalRecords'], items.length);
    const totalPages = this.toPositiveNumber(
      record['totalPages'],
      Math.max(1, Math.ceil(totalRecords / fallbackPageSize)),
    );

    return {
      pageNumber: this.toPositiveNumber(record['pageNumber'], fallbackPageNumber),
      pageSize: this.toPositiveNumber(record['pageSize'], fallbackPageSize),
      totalRecords,
      totalPages,
      items,
    };
  }

  private normalizeItem(item: BondApiItem): BondRow {
    return {
      dealId: this.toText(item.dealId),
      tradeId: this.toText(item.tradeId),
      structureId: this.toText(item.structureId),
      securityId: this.toText(item.securityId),
      cusip: this.toIdentifier(item.cusip),
      isin: this.toIdentifier(item.isin),
      securityType: this.toText(item.securityType),
      tradeDate: this.toText(item.tradeDate),
      settlementDate: this.toText(item.settlementDate),
      maturityDate: this.toText(item.maturityDate),
      currency: this.toText(item.currency),
      notional: this.toNumber(item.notional),
      coupon: this.toNumber(item.coupon),
      marketType: this.toText(item.marketType),
    };
  }

  private normalizePageNumber(value: number | undefined): number {
    return this.toPositiveNumber(value, this.defaultPageNumber);
  }

  private normalizePageSize(value: number | undefined): number {
    const parsed = this.toPositiveNumber(value, this.defaultPageSize);

    if (parsed < this.minPageSize) {
      return this.minPageSize;
    }

    if (parsed > this.maxPageSize) {
      return this.maxPageSize;
    }

    return parsed;
  }

  private normalizeText(value: string | undefined): string {
    return typeof value === 'string' ? value.trim() : '';
  }

  private toRecord(value: unknown): Record<string, unknown> {
    if (value && typeof value === 'object') {
      return value as Record<string, unknown>;
    }

    return {};
  }

  private toItems(value: unknown): BondApiItem[] {
    return Array.isArray(value) ? (value as BondApiItem[]) : [];
  }

  private toText(value: unknown): string {
    return typeof value === 'string' ? value : '';
  }

  // Source columns can be space-padded or contain a literal "null"; treat both as missing.
  private toIdentifier(value: unknown): string {
    const text = this.toText(value).trim();
    return text.toLowerCase() === 'null' ? '' : text;
  }

  private toNumber(value: unknown): number {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    if (typeof value === 'string') {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : 0;
    }

    return 0;
  }

  private toPositiveNumber(value: unknown, fallback: number): number {
    const parsed = this.toNumber(value);
    return parsed > 0 ? parsed : fallback;
  }

  private toNonNegativeNumber(value: unknown, fallback: number): number {
    if (value === null || value === undefined || value === '') {
      return fallback;
    }

    const parsed = this.toNumber(value);
    return parsed >= 0 ? parsed : fallback;
  }
}
